import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2, MapPin, DoorClosed, Loader2, Navigation, GitMerge, Archive, Undo2, Camera } from "lucide-react";
import { buildingsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import { buildBuildingAddress, directionsUrl } from "@/lib/directions";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PhotoCapture } from "@/components/PhotoCapture";

function NewBuildingDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState("");
  const [form, setForm] = useState({ name: "", address: "", postcode: "", client_name: "", access_codes: "", parking_details: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim() || !form.postcode.trim()) {
      toast.error("Name, address and postcode are required.");
      return;
    }
    setSaving(true);
    try {
      const res = await buildingsApi.create({ ...form, photo_base64: photo || undefined });
      toast.success("Building created");
      setOpen(false);
      setForm({ name: "", address: "", postcode: "", client_name: "", access_codes: "", parking_details: "" });
      setPhoto("");
      onCreated?.(res.data);
    } catch (err) {
      toast.error(apiError(err, "Could not create building"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-building-btn">
          <Plus className="mr-2 h-4 w-4" /> New building
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New building</DialogTitle>
          <DialogDescription>Add a property to your fire-door register.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Building photo</Label>
            <PhotoCapture photos={photo ? [photo] : []} onChange={(ph) => setPhoto(ph[0] || "")} max={1} testId="building-photo" />
            {!photo && (
              <p className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700" data-testid="building-photo-reminder">
                <Camera className="h-3.5 w-3.5 shrink-0" /> Tip: add a building photo — it appears on report covers. You can still save without one.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="b-name">Building name *</Label>
            <Input id="b-name" value={form.name} onChange={set("name")} placeholder="e.g. Hunter House" data-testid="building-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="b-address">Address *</Label>
            <Input id="b-address" value={form.address} onChange={set("address")} placeholder="Street address" data-testid="building-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="b-postcode">Postcode *</Label>
              <Input id="b-postcode" value={form.postcode} onChange={set("postcode")} placeholder="AB1 2CD" data-testid="building-postcode-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-client">Client name</Label>
              <Input id="b-client" value={form.client_name} onChange={set("client_name")} placeholder="Optional" data-testid="building-client-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="b-access">Access codes</Label>
            <Input id="b-access" value={form.access_codes} onChange={set("access_codes")} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="b-parking">Parking details</Label>
            <Input id="b-parking" value={form.parking_details} onChange={set("parking_details")} placeholder="Optional" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="building-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create building
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MergeBuildingsDialog({ buildings, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [keeperId, setKeeperId] = useState("");
  const [sourceId, setSourceId] = useState("");

  const reset = () => { setKeeperId(""); setSourceId(""); };
  const nameFor = (bid) => buildings.find((b) => b.id === bid)?.name || "";

  const merge = async () => {
    if (!keeperId || !sourceId) { toast.error("Choose both buildings"); return; }
    if (keeperId === sourceId) { toast.error("Choose two different buildings"); return; }
    if (!window.confirm(`Merge "${nameFor(sourceId)}" into "${nameFor(keeperId)}"? All its doors move across. You can reverse this later from Archived.`)) return;
    setBusy(true);
    try {
      const { data } = await buildingsApi.merge(keeperId, sourceId);
      toast.success(`Merged — ${data.doors_moved} door${data.doors_moved === 1 ? "" : "s"} moved`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(apiError(e, "Could not merge buildings")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="merge-buildings-btn"><GitMerge className="mr-2 h-4 w-4" /> Merge</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Merge duplicate buildings</DialogTitle>
          <DialogDescription>Keep one building and move the duplicate's doors into it. The duplicate is archived and can be restored later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Keep this building</Label>
            <Select value={keeperId} onValueChange={setKeeperId}>
              <SelectTrigger data-testid="merge-keeper-select"><SelectValue placeholder="Select the building to keep" /></SelectTrigger>
              <SelectContent>
                {buildings.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`merge-keeper-${b.id}`}>{b.name} · {b.door_count ?? 0} doors</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Merge this one into it (archived)</Label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger data-testid="merge-source-select"><SelectValue placeholder="Select the duplicate to merge" /></SelectTrigger>
              <SelectContent>
                {buildings.filter((b) => b.id !== keeperId).map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`merge-source-${b.id}`}>{b.name} · {b.door_count ?? 0} doors</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy} onClick={merge} data-testid="merge-confirm-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Merge buildings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedBuildingsDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await buildingsApi.archived(); setList(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(apiError(e, "Could not load archived buildings")); }
    finally { setLoading(false); }
  };

  const unmerge = async (a) => {
    if (!window.confirm(`Restore "${a.name}" and move its ${a.doors_moved} door${a.doors_moved === 1 ? "" : "s"} back out of "${a.merged_into_name || "the merged building"}"?`)) return;
    setBusyId(a.id);
    try {
      await buildingsApi.unmerge(a.id);
      toast.success("Building restored");
      await load();
      onDone?.();
    } catch (e) { toast.error(apiError(e, "Could not restore building")); }
    finally { setBusyId(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="archived-buildings-btn"><Archive className="mr-2 h-4 w-4" /> Archived</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Archived &amp; merged buildings</DialogTitle>
          <DialogDescription>Buildings merged into another. Restore one to move its doors back.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <LoadingState label="Loading…" />
        ) : list.length === 0 ? (
          <EmptyState icon={Archive} title="Nothing archived" message="Merged buildings will appear here so you can reverse a merge." />
        ) : (
          <div className="space-y-2">
            {list.map((a) => (
              <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`archived-row-${a.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900">{a.name}</p>
                  <p className="truncate text-sm text-slate-500">
                    Merged into <span className="font-medium text-slate-700">{a.merged_into_name || "—"}</span> · {a.doors_moved} door{a.doors_moved === 1 ? "" : "s"}
                  </p>
                </div>
                <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => unmerge(a)} data-testid={`unmerge-btn-${a.id}`}>
                  {busyId === a.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Undo2 className="mr-1.5 h-4 w-4" />} Restore
                </Button>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Buildings() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => buildingsApi.getAll(), []);
  const [q, setQ] = useState("");

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(s) ||
        (b.address || "").toLowerCase().includes(s) ||
        (b.postcode || "").toLowerCase().includes(s) ||
        (b.client_name || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  return (
    <div>
      <PageHeader
        title="Buildings"
        subtitle="Your fire-door register, organised by property"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {list.length >= 2 && <MergeBuildingsDialog buildings={list} onDone={reload} />}
            <ArchivedBuildingsDialog onDone={reload} />
            <NewBuildingDialog onCreated={() => reload()} />
          </div>
        }
        testId="buildings-header"
      />

      {loading && <LoadingState label="Loading buildings…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search buildings…"
              className="pl-10"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="buildings-search"
            />
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title={list.length === 0 ? "No buildings yet" : "No matches"}
              message={list.length === 0 ? "Add your first building to start managing fire-door inspections." : "Try a different search term."}
              action={list.length === 0 ? <NewBuildingDialog onCreated={() => reload()} /> : null}
              testId="buildings-empty"
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="buildings-grid">
              {filtered.map((b) => (
                <Card
                  key={b.id}
                  className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
                  onClick={() => navigate(`/buildings/${b.id}`)}
                  data-testid={`building-card-${b.id}`}
                >
                  <div className="flex items-start gap-3 p-5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-slate-900">{b.name}</h3>
                      <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{[b.address, b.postcode].filter(Boolean).join(", ") || "No address"}</span>
                      </p>
                      {b.client_name && <p className="mt-1 truncate text-xs text-slate-400">Client: {b.client_name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-5 py-2.5 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <DoorClosed className="h-4 w-4 text-slate-400" />
                      {b.door_count ?? 0} {b.door_count === 1 ? "door" : "doors"}
                    </span>
                    {(() => {
                      const dir = directionsUrl(buildBuildingAddress(b));
                      if (!dir) return null;
                      return (
                        <a
                          href={dir}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                          data-testid={`building-directions-${b.id}`}
                        >
                          <Navigation className="h-3.5 w-3.5" /> Directions
                        </a>
                      );
                    })()}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
